import {
  alpha,
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  Popover,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import NotificationsActiveTwoToneIcon from '@mui/icons-material/NotificationsActiveTwoTone';
import { keyframes, styled } from '@mui/material/styles';

import {
  useCheckAllNotificationMutation,
  useListNotification,
  useNotificationUncheck,
} from '@/apis/service/notifications';
import { INotificationItem, NotificationList } from '@/interfaces';
import i18n from '@/i18n';
import { useModalContext } from '@/components/ModalProvider';
import { COLORS } from '@/global-styles';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/ko';
import 'dayjs/locale/en';

dayjs.extend(relativeTime);

const bellWiggle = keyframes`
  0%, 72% {
    transform: rotate(0deg);
  }
  76% {
    transform: rotate(14deg);
  }
  80% {
    transform: rotate(-12deg);
  }
  84% {
    transform: rotate(9deg);
  }
  88% {
    transform: rotate(-6deg);
  }
  92% {
    transform: rotate(3deg);
  }
  96%,
  100% {
    transform: rotate(0deg);
  }
`;

const BADGE_COUNT_TRANSITION_MS = 420;
const badgeCountEase = 'cubic-bezier(0.22, 1, 0.36, 1)';

const rollDigitUp = keyframes`
  from { transform: translateY(0); }
  to { transform: translateY(-50%); }
`;

const rollDigitDown = keyframes`
  from { transform: translateY(-50%); }
  to { transform: translateY(0); }
`;

const digitColumnSx = {
  display: 'inline-block',
  height: '1em',
  overflow: 'hidden',
  lineHeight: 1,
  verticalAlign: 'top',
  fontVariantNumeric: 'tabular-nums',
  WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, #000 18%, #000 82%, transparent 100%)',
  maskImage: 'linear-gradient(to bottom, transparent 0%, #000 18%, #000 82%, transparent 100%)',
} as const;

const digitCellSx = {
  display: 'block',
  height: '1em',
  lineHeight: 1,
  textAlign: 'center',
} as const;

function formatBadgeCount(count: number): string {
  if (count > 99) return '99+';
  return String(count);
}

type RollingDigitProps = {
  from: string;
  to: string;
  animate: boolean;
  reduceMotion: boolean;
};

function RollingDigit({ from, to, animate, reduceMotion }: RollingDigitProps) {
  if (reduceMotion || !animate || from === to) {
    return (
      <Box component="span" sx={digitColumnSx}>
        <Box component="span" sx={digitCellSx}>
          {to}
        </Box>
      </Box>
    );
  }

  const goingUp = Number(to) > Number(from);
  const digits = goingUp ? [from, to] : [to, from];

  return (
    <Box component="span" sx={digitColumnSx}>
      <Box
        component="span"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          animation: `${goingUp ? rollDigitUp : rollDigitDown} ${BADGE_COUNT_TRANSITION_MS}ms ${badgeCountEase} forwards`,
          ...(goingUp ? { transform: 'translateY(0)' } : { transform: 'translateY(-50%)' }),
        }}>
        {digits.map((digit, index) => (
          <Box component="span" key={`${digit}-${index}`} sx={digitCellSx}>
            {digit}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

type RollingTextProps = {
  from: string;
  to: string;
  animate: boolean;
  reduceMotion: boolean;
};

function RollingText({ from, to, animate, reduceMotion }: RollingTextProps) {
  if (reduceMotion || !animate || from === to) {
    return <>{to}</>;
  }

  const goingUp = to.length > from.length || to > from;
  const rows = goingUp ? [from, to] : [to, from];

  return (
    <Box component="span" sx={digitColumnSx}>
      <Box
        component="span"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          whiteSpace: 'nowrap',
          animation: `${goingUp ? rollDigitUp : rollDigitDown} ${BADGE_COUNT_TRANSITION_MS}ms ${badgeCountEase} forwards`,
          ...(goingUp ? { transform: 'translateY(0)' } : { transform: 'translateY(-50%)' }),
        }}>
        {rows.map((row, index) => (
          <Box component="span" key={`${row}-${index}`} sx={digitCellSx}>
            {row}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

type AnimatedBadgeCountProps = {
  count: number;
  reduceMotion: boolean;
};

function AnimatedBadgeCount({ count, reduceMotion }: AnimatedBadgeCountProps) {
  const display = formatBadgeCount(count);
  const prevDisplayRef = useRef(display);
  const [transition, setTransition] = useState<{ from: string; to: string; active: boolean }>({
    from: display,
    to: display,
    active: false,
  });

  useEffect(() => {
    const from = prevDisplayRef.current;
    const to = display;
    if (from === to) return;

    prevDisplayRef.current = to;
    setTransition({ from, to, active: true });
    const timer = window.setTimeout(() => {
      setTransition((prev) => ({ ...prev, active: false }));
    }, BADGE_COUNT_TRANSITION_MS);

    return () => window.clearTimeout(timer);
  }, [display]);

  const { from, to, active } = transition;
  const useWholeRoll = from.length !== to.length || from.includes('+') || to.includes('+');

  if (useWholeRoll) {
    return (
      <Box
        component="span"
        className="animated-badge-count"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1,
        }}>
        <RollingText from={from} to={to} animate={active} reduceMotion={reduceMotion} />
      </Box>
    );
  }

  return (
    <Box
      component="span"
      className="animated-badge-count"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontVariantNumeric: 'tabular-nums',
        lineHeight: 1,
      }}>
      {to.split('').map((nextDigit, index) => (
        <RollingDigit
          key={index}
          from={from[index] ?? nextDigit}
          to={nextDigit}
          animate={active}
          reduceMotion={reduceMotion}
        />
      ))}
    </Box>
  );
}

const NotificationsBadge = styled(Badge)(
  ({ theme }) => `
    
    .MuiBadge-badge {
        background-color: ${alpha(theme.palette.error.main, 0.1)};
        color: ${theme.palette.error.main};
        min-width: 16px; 
        height: 16px;
        padding: 0 4px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 0.65rem;
        font-weight: 700;
        line-height: 1;

        .animated-badge-count {
          font-size: inherit;
          font-weight: inherit;
          line-height: 1;
        }

        &::after {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            border-radius: 50%;
            box-shadow: 0 0 0 1px ${alpha(theme.palette.error.main, 0.3)};
            content: "";
        }
    }
`,
);
const NotiMessageItem: FC<INotificationItem & { onClosePopover: () => void }> = ({
  id,
  title,
  content,
  time_send,
  onClosePopover,
  sender,
  status,
}) => {
  const modalContext = useModalContext();

  return (
    <ListItem
      onClick={() => {
        onClosePopover();
        modalContext.onShow({
          id,
          hasFooter: true,
        });
      }}
      key={id}
      sx={{
        p: 2,
        minWidth: 350,
        display: { xs: 'block', sm: 'flex', backgroundColor: status !== 'SEEN' ? COLORS.light_grey : '' },
      }}>
      <Box flex="1">
        <Box display="flex" justifyContent="space-between">
          <Typography sx={{ fontWeight: 'bold', maxWidth: 250 }}>{title}</Typography>
          <Typography variant="caption" sx={{ textTransform: 'none' }}>
            {dayjs(time_send)
              .locale(i18n.language === 'kr' ? 'ko' : 'en')
              .fromNow()}
          </Typography>
        </Box>
        <Typography
          // clamp 3 lines
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: '3',
            WebkitBoxOrient: 'vertical',
          }}
          component="p"
          color="text.secondary">
          {content}
        </Typography>
      </Box>
    </ListItem>
  );
};
const DEFAULT_PAGE_NO = 0;
const DEFAULT_PAGE_SIZE = 10;

type NotificationListView = 'loading' | 'empty' | 'content';

function getNotificationListView(
  notiList: NotificationList | undefined,
  isLoading: boolean,
): NotificationListView {
  const hasItems = Boolean(notiList?.content?.length);
  if (!hasItems && isLoading) return 'loading';
  if (!hasItems) return 'empty';
  return 'content';
}

type RenderNotificationListBodyParams = {
  view: NotificationListView;
  notiList: NotificationList | undefined;
  isLastPage: boolean;
  isListLoading: boolean;
  emptyMessage: string;
  onClosePopover: () => void;
  onLoadMore: () => void;
};

function renderNotificationListBody({
  view,
  notiList,
  isLastPage,
  isListLoading,
  emptyMessage,
  onClosePopover,
  onLoadMore,
}: RenderNotificationListBodyParams): ReactNode {
  switch (view) {
    case 'loading':
      return (
        <ListItem sx={{ justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </ListItem>
      );
    case 'empty':
      return (
        <ListItem sx={{ justifyContent: 'center', py: 4, px: 2 }}>
          <Typography variant="body2" color="text.secondary" align="center">
            {emptyMessage}
          </Typography>
        </ListItem>
      );
    case 'content':
      return (
        <>
          {notiList!.content.map((item, index) => (
            <div style={{ cursor: 'pointer' }} key={index + 'itemNoti'}>
              <NotiMessageItem {...item} onClosePopover={onClosePopover} />
            </div>
          ))}
          {!isLastPage && (
            <ListItem sx={{ minWidth: 350, display: { xs: 'block', sm: 'flex' } }}>
              <Button disabled={isListLoading} onClick={onLoadMore} sx={{ fontWeight: 'bold', flex: 1 }}>
                Load more
              </Button>
            </ListItem>
          )}
        </>
      );
    default:
      return null;
  }
}

function HeaderNotifications() {
  const { t } = useTranslation();
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const ref = useRef(null);
  const [isOpen, setOpen] = useState<boolean>(false);
  const [notiList, setNotiList] = useState<NotificationList>();
  const { data, refetch } = useNotificationUncheck();

  const { mutateAsync, isLoading: isCheckAllLoading } = useCheckAllNotificationMutation();
  const { mutateAsync: mutateGetList, isLoading: isListLoading } = useListNotification();
  const isNotiMutating = isCheckAllLoading || isListLoading;
  const handleOpen = (): void => {
    setOpen(true);
    mutateAsync().then(async () => {
      const dataList = await mutateGetList({
        page_no: DEFAULT_PAGE_NO,
        page_size: DEFAULT_PAGE_SIZE,
        unread_only: true,
      });
      setNotiList(dataList.data);
      refetch({ stale: true });
    });
    //   if (data) {
    // }
  };

  const handleClose = (): void => {
    setOpen(false);
  };
  const isLastPage = useMemo(() => {
    if (!notiList) return true;
    return notiList.totalElements <= notiList?.numberOfElements;
  }, [notiList]);

  const unreadCount = (data?.data && data.data.count) || 0;
  const showBellMotion = unreadCount > 0 && !isOpen;
  const listView = getNotificationListView(notiList, isNotiMutating);

  const handleLoadMore = (): void => {
    if (!notiList) return;
    mutateGetList({
      page_no: DEFAULT_PAGE_NO,
      page_size: DEFAULT_PAGE_SIZE + notiList.numberOfElements,
      unread_only: true,
    }).then((dataListNoti) => {
      setNotiList(dataListNoti.data);
    });
  };

  return (
    <>
      <Tooltip arrow title="Notifications">
        <IconButton color="primary" ref={ref} onClick={handleOpen}>
          <NotificationsBadge
            invisible={unreadCount < 1}
            badgeContent={<AnimatedBadgeCount count={unreadCount} reduceMotion={reduceMotion} />}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}>
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                transformOrigin: '50% 0%',
                ...(showBellMotion && {
                  animation: `${bellWiggle} 2.6s ease-in-out infinite`,
                }),
              }}>
              <NotificationsActiveTwoToneIcon />
            </Box>
          </NotificationsBadge>
        </IconButton>
      </Tooltip>
      <Popover
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}>
        <Box sx={{ p: 2 }} display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h5">Notifications</Typography>
        </Box>
        <Divider />
        <Box sx={{ position: 'relative', width: 350 }}>
          {isNotiMutating ? (
            <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1 }} />
          ) : null}
          <List sx={{ p: 0, width: 350, pt: isNotiMutating ? 0.5 : 0 }}>
            {renderNotificationListBody({
              view: listView,
              notiList,
              isLastPage,
              isListLoading,
              emptyMessage: t('notifications.no_unread'),
              onClosePopover: handleClose,
              onLoadMore: handleLoadMore,
            })}
          </List>
        </Box>
      </Popover>
    </>
  );
}

export default HeaderNotifications;
